import React, { useState, useEffect, useMemo } from "react";
import Select from 'react-select';
import { debounce } from 'lodash';
import { toast } from 'react-hot-toast';

// API Hooks
import { useDirectInputApi } from "../../store/api/hooks/directInputApiHook";
import { useCountryApi } from "../../store/api/hooks/countryApiHook";
import { useRegionApi } from "../../store/api/hooks/regionApiHook";
import { useSubRegionApi } from "../../store/api/hooks/subRegionApiHook";
import { useAppellationApi } from "../../store/api/hooks/appellationApiHook";
import { useSubAppellationApi } from "../../store/api/hooks/subAppellationApiHook";

// Components
import Button from "../../components/Buttons";
import LoadingSpinner from "../../components/LoadingSpinner";

// Icons
import { CloseIcon } from "../../assets/Icons";

// Type Definitions
interface Producer {
    id: number;
    name: string;
    name_show: string;
    literal?: string; // Add literal property
}

interface Wine {
    id: number;
    literal: string;
    name: string;
    color: string;
    type: string;
    sweetness: string;
    variety_id: number;
    producer_id: number;
    producer: Producer;
    wine_country: number;
    wine_region: number;
    wine_location: number;
    wine_locale?: number;
    wine_site?: number;
}

interface MergeWinesModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedWines: Wine[];
    onMergeSuccess?: () => void;
}

interface MergeDataItem {
    source: string;
    value: string;
    producerId?: string;
    producerName?: string;
    regCode?: string;
    nameShow?: string;
}

interface SelectOption {
    value: string;
    label: string;
    region_code?: string;
}

interface ApiOptions {
    color: SelectOption[];
    sweetness: SelectOption[];
    type: SelectOption[];
    variety: SelectOption[];
    country: SelectOption[];
    region: (SelectOption & { region_code: string })[];
    subRegion: SelectOption[];
    Appellation: SelectOption[];
    subAppellation: SelectOption[];
}

// Field configuration for form rendering
const FORM_FIELDS = [
    { key: "literal", label: "ID", type: "input" },
    {
        key: "producer",
        label: "Producer",
        type: "producer",
        customInputs: [
            { key: "producer_id", type: "producer-select", label: "Producer" },
            { key: "reg_code", type: "reg-code-select", label: "Reg Code" },
            { key: "name_show", type: "text", label: "Name Show" }
        ]
    },
    { key: "name", label: "Name", type: "input" },
    { key: "color", label: "Color", type: "select" },
    { key: "type", label: "Type", type: "select" },
    { key: "sweetness", label: "Sweetness", type: "select" },
    { key: "variety", label: "Variety", type: "select" },
    { key: "wine_country", label: "Country", type: "location-select" },
    { key: "wine_region", label: "Region", type: "location-select" },
    { key: "wine_sub_region", label: "Sub Region", type: "location-select" },
    { key: "wine_appellation", label: "Appellation", type: "location-select" },
    { key: "wine_sub_appellation", label: "Sub Appellation", type: "location-select" },
];

// EntityMap type for ID-to-name mapping
interface EntityMap {
    [key: number]: string;
}

const MergeWinesModal: React.FC<MergeWinesModalProps> = ({
    isOpen,
    onClose,
    selectedWines,
    onMergeSuccess,
}) => {
    // State Management
    const [mergeData, setMergeData] = useState<Record<string, MergeDataItem>>({});
    const [apiOptions, setApiOptions] = useState<ApiOptions>({
        color: [],
        sweetness: [],
        variety: [],
        type: [],
        country: [],
        region: [],
        subRegion: [],
        Appellation: [],
        subAppellation: [],
    });
    const [isLoading, setIsLoading] = useState(false);
    const [producers, setProducers] = useState<Array<{ value: string, label: string, name_show: string, literal: string }>>([]);
    const [producerPage, setProducerPage] = useState(1);
    const [hasMoreProducers, setHasMoreProducers] = useState(false);
    const [isLoadingProducers, setIsLoadingProducers] = useState(false);
    const [producerSearchTerm, setProducerSearchTerm] = useState('');
    const [isProducerDisabled, setIsProducerDisabled] = useState(false);
    const [isRegCodeDisabled, setIsRegCodeDisabled] = useState(false);
    const [isLoadingLiteral, setIsLoadingLiteral] = useState(false);
    const [regCodeOptions, setRegCodeOptions] = useState<Array<{ value: string; label: string }>>([]);



    // Entity maps for display
    const [countryMap, setCountryMap] = useState<EntityMap>({});
    const [regionMap, setRegionMap] = useState<EntityMap>({});
    const [subRegionMap, setSubRegionMap] = useState<EntityMap>({});
    const [appellationMap, setAppellationMap] = useState<EntityMap>({});
    const [subAppellationMap, setSubAppellationMap] = useState<EntityMap>({});

    // Location loading states
    const [isLoadingRegions, setIsLoadingRegions] = useState(false);
    const [isLoadingSubRegions, setIsLoadingSubRegions] = useState(false);
    const [isLoadingAppellations, setIsLoadingAppellations] = useState(false);
    const [isLoadingSubAppellations, setIsLoadingSubAppellations] = useState(false);

    // API Hooks Initialization
    const directInputApi = useDirectInputApi();
    const countryApi = useCountryApi();
    const regionApi = useRegionApi();
    const subRegionApi = useSubRegionApi();
    const AppellationApi = useAppellationApi();
    const subAppellationApi = useSubAppellationApi();

    const [countryIdByName, setCountryIdByName] = useState<string | null>(null);
    const [regionIdByName, setRegionIdByName] = useState<string | null>(null);
    const [subRegionIdByName, setSubRegionIdByName] = useState<string | null>(null);
    const [appellationIdByName, setAppellationIdByName] = useState<string | null>(null);
    const [subAppellationIdByName, setSubAppellationIdByName] = useState<string | null>(null);

    const fetchRegionRegCodes = async () => {
        try {
            const response = await regionApi.getRegionList({ per_page: 999999 });
            const regCodesArr = response?.data?.data?.result || [];
            // Remove duplicates and sort
            const uniqueCodes = Array.from(
                new Set(regCodesArr.map((reg: any) => reg.region_code))
            ).filter(Boolean); // Remove falsy values if any
            const regCodeOptions = Array.from(uniqueCodes as string[])
                .sort()
                .map((code: string) => ({
                    value: code,
                    label: code
                }));
            setRegCodeOptions(regCodeOptions);
        } catch (error) {
            console.error('Error fetching region reg codes:', error);
            setRegCodeOptions([]);
        }
    };

    // Function to fetch producers with search
    const fetchProducers = async (page: number, searchTerm: string = '', resetList: boolean = false) => {
        try {
            setIsLoadingProducers(true);
            const response = await directInputApi.getProducer({ 
                page, 
                per_page: 15,
                ...(searchTerm && { keywords: searchTerm })
            });
            
            const data = response?.data?.data?.result;
            const newProducers = (data || []).map((producer: any) => ({
                value: producer.id,
                label: producer.name_show || producer.name,
                name_show: producer.name_show,
                literal: producer.literal,
            }));
            if (resetList || page === 1) {
                setProducers(newProducers);
            } else {
                setProducers(prev => [...prev, ...newProducers]);
            }

            // Check if there is a next page
            setHasMoreProducers(!!response?.data?.data?.next);
            setProducerPage(page);
        } catch (error) {
            console.error('Error fetching producers:', error);
        } finally {
            setIsLoadingProducers(false);
        }
    };

    // Location fetching functions
    const fetchRegionsByCountry = async (countryId: string) => {
        if (!countryId) {
            setApiOptions(prev => ({ ...prev, region: [], subRegion: [], Appellation: [], subAppellation: [] }));
            return;
        }
        
        try {
            setIsLoadingRegions(true);
            const response = await regionApi.getRegionList({ 
                per_page: 999999,
                country_id: countryId
            });
            const regionArr = response?.data?.data?.result || [];
            const regionOptions = regionArr.map((region: any) => ({
                value: region.id.toString(),
                label: region.name,
                region_code: region.region_code
            }));
            setApiOptions(prev => ({ 
                ...prev, 
                region: regionOptions,
                subRegion: [],
                Appellation: [],
                subAppellation: []
            }));
            // Do NOT update regionMap here
        } catch (error) {
            console.error('Error fetching regions:', error);
        } finally {
            setIsLoadingRegions(false);
        }
    };

    const fetchSubRegionsByRegion = async (regionId: string) => {
        if (!regionId) {
            setApiOptions(prev => ({ ...prev, subRegion: [], Appellation: [], subAppellation: [] }));
            return;
        }
        
        try {
            setIsLoadingSubRegions(true);
            const response = await subRegionApi.getSubRegionList({ 
                per_page: 999999,
                region_id: regionId
            });
            const subRegionArr = response?.data?.data?.result || [];
            const subRegionOptions = subRegionArr.map((subRegion: any) => ({
                value: subRegion.id.toString(),
                label: subRegion.name
            }));
            setApiOptions(prev => ({ 
                ...prev, 
                subRegion: subRegionOptions,
                Appellation: [],
                subAppellation: []
            }));
            // Do NOT update subRegionMap here
        } catch (error) {
            console.error('Error fetching sub regions:', error);
        } finally {
            setIsLoadingSubRegions(false);
        }
    };

    const fetchAppellationsBySubRegion = async (subRegionId: string) => {
        if (!subRegionId) {
            setApiOptions(prev => ({ ...prev, Appellation: [], subAppellation: [] }));
            return;
        }
        
        try {
            setIsLoadingAppellations(true);
            const response = await AppellationApi.getAppellationList({ 
                per_page: 999999,
                sub_region_id: subRegionId
            });
            const appellationArr = response?.data?.data?.result || [];
            const appellationOptions = appellationArr.map((appellation: any) => ({
                value: appellation.id.toString(),
                label: appellation.name
            }));
            setApiOptions(prev => ({ 
                ...prev, 
                Appellation: appellationOptions,
                subAppellation: []
            }));
            // Do NOT update appellationMap here
        } catch (error) {
            console.error('Error fetching appellations:', error);
        } finally {
            setIsLoadingAppellations(false);
        }
    };

    const fetchSubAppellationsByAppellation = async (appellationId: string) => {
        if (!appellationId) {
            setApiOptions(prev => ({ ...prev, subAppellation: [] }));
            return;
        }
        
        try {
            setIsLoadingSubAppellations(true);
            const response = await subAppellationApi.getSubAppellationList({ 
                per_page: 999999,
                appellation_id: appellationId
            });
            const subAppellationArr = response?.data?.data?.result || [];
            const subAppellationOptions = subAppellationArr.map((subAppellation: any) => ({
                value: subAppellation.id.toString(),
                label: subAppellation.name
            }));
            setApiOptions(prev => ({ 
                ...prev, 
                subAppellation: subAppellationOptions
            }));
            // Do NOT update subAppellationMap here
        } catch (error) {
            console.error('Error fetching sub appellations:', error);
        } finally {
            setIsLoadingSubAppellations(false);
        }
    };

    // Helper to get ID from name via API if needed
    const getIdFromNameApi = async (type: 'country' | 'region' | 'subRegion' | 'appellation' | 'subAppellation', name: string): Promise<string> => {
        let api, param;
        switch (type) {
            case 'country': api = countryApi.getCountryList; param = { search: name }; break;
            case 'region': api = regionApi.getRegionList; param = { search: name }; break;
            case 'subRegion': api = subRegionApi.getSubRegionList; param = { search: name }; break;
            case 'appellation': api = AppellationApi.getAppellationList; param = { search: name }; break;
            case 'subAppellation': api = subAppellationApi.getSubAppellationList; param = { search: name }; break;
            default: return '';
        }
        try {
            const res = await api(param);
            const result = res?.data?.data?.result?.[0];
            const id = result?.id?.toString() || '';
            // Set the corresponding state for downstream usage
            if (type === 'country') setCountryIdByName(id);
            else if (type === 'region') setRegionIdByName(id);
            else if (type === 'subRegion') setSubRegionIdByName(id);
            else if (type === 'appellation') setAppellationIdByName(id);
            else if (type === 'subAppellation') setSubAppellationIdByName(id);
            return id;
        } catch {
            return '';
        }
    };

    // Helper to validate location hierarchy and clear incompatible selections
    const validateLocationHierarchy = async (fieldKey: string, source: string, selectedWine: Wine) => {
        const locationFields = ['wine_country', 'wine_region', 'wine_sub_region', 'wine_appellation', 'wine_sub_appellation'];
        const fieldIndex = locationFields.indexOf(fieldKey);
        
        if (fieldIndex === -1) return {}; // Not a location field
        
        const fieldsToValidate: Record<string, MergeDataItem> = {};
        
        // Get the wine that corresponds to the selected source
        if (source === 'custom') {
            // If selecting custom, we don't need to validate hierarchy with other wines
            return fieldsToValidate;
        }
        
        // Check if any parent fields are selected from different wines
        for (let i = 0; i < fieldIndex; i++) {
            const parentField = locationFields[i];
            const parentSelection = mergeData[parentField];
            
            if (parentSelection && parentSelection.source !== source && parentSelection.source !== 'custom') {
                // Parent field is from a different wine - need to validate hierarchy
                const parentWine = selectedWines.find(w => w.id.toString() === parentSelection.source);
                if (parentWine) {
                    // Check if the selected wine's parent location matches the parent wine's location
                    let isCompatible = false;
                    
                    if (parentField === 'wine_country' && fieldKey === 'wine_region') {
                        isCompatible = selectedWine.wine_country === parentWine.wine_country;
                    } else if (parentField === 'wine_country' && fieldKey === 'wine_sub_region') {
                        isCompatible = selectedWine.wine_country === parentWine.wine_country;
                    } else if (parentField === 'wine_region' && fieldKey === 'wine_sub_region') {
                        isCompatible = selectedWine.wine_region === parentWine.wine_region;
                    } else if (parentField === 'wine_sub_region' && fieldKey === 'wine_appellation') {
                        isCompatible = selectedWine.wine_location === parentWine.wine_location;
                    } else if (parentField === 'wine_appellation' && fieldKey === 'wine_sub_appellation') {
                        isCompatible = selectedWine.wine_locale === parentWine.wine_locale;
                    }
                    
                    // Also check multi-level hierarchies
                    if (parentField === 'wine_country' && fieldKey === 'wine_appellation') {
                        // Need to check country -> region -> sub_region -> appellation chain
                        const regionSelection = mergeData['wine_region'];
                        const subRegionSelection = mergeData['wine_sub_region'];
                        
                        if (regionSelection && subRegionSelection) {
                            const regionWine = selectedWines.find(w => w.id.toString() === regionSelection.source);
                            const subRegionWine = selectedWines.find(w => w.id.toString() === subRegionSelection.source);
                            
                            isCompatible = selectedWine.wine_country === parentWine.wine_country &&
                                          (regionWine ? selectedWine.wine_region === regionWine.wine_region : true) &&
                                          (subRegionWine ? selectedWine.wine_location === subRegionWine.wine_location : true);
                        } else {
                            isCompatible = selectedWine.wine_country === parentWine.wine_country;
                        }
                    }
                    
                    if (parentField === 'wine_country' && fieldKey === 'wine_sub_appellation') {
                        // Check full hierarchy
                        isCompatible = selectedWine.wine_country === parentWine.wine_country;
                    }
                    
                    if (parentField === 'wine_region' && fieldKey === 'wine_appellation') {
                        isCompatible = selectedWine.wine_region === parentWine.wine_region;
                    }
                    
                    if (parentField === 'wine_region' && fieldKey === 'wine_sub_appellation') {
                        isCompatible = selectedWine.wine_region === parentWine.wine_region;
                    }
                    
                    if (parentField === 'wine_sub_region' && fieldKey === 'wine_sub_appellation') {
                        isCompatible = selectedWine.wine_location === parentWine.wine_location;
                    }
                    
                    if (!isCompatible) {
                        // Clear the incompatible parent field
                        fieldsToValidate[parentField] = { source: '', value: '' };
                    }
                }
            }
        }
        
        return fieldsToValidate;
    };

    // Handle location field changes with waterfall effect
    const handleLocationFieldChange = async (fieldKey: string, value: string) => {
         // If value is not a number, get ID from name via API
        let idValue = value;
        if (typeof value === 'string' && isNaN(Number(value)) && value) {
            let type: any = '';
            if (fieldKey === 'wine_country') type = 'country';
            else if (fieldKey === 'wine_region') type = 'region';
            else if (fieldKey === 'wine_sub_region') type = 'subRegion';
            else if (fieldKey === 'wine_appellation') type = 'appellation';
            else if (fieldKey === 'wine_sub_appellation') type = 'subAppellation';
            if (type) {
                idValue = await getIdFromNameApi(type, value);
            }
        }

        handleFieldChange(fieldKey, 'custom', idValue);
        // // Helper to get ID from name if needed
        // const getIdFromName = (options: SelectOption[], val: string) => {
        //     if (typeof val === 'string' && isNaN(Number(val))) {
        //         const found = options.find(opt => opt.label === val);
        //         return found ? found.value : val;
        //     }
        //     return val;
        // };

        // // Determine correct ID for API call
        // let idValue = value;
        // if (fieldKey === 'wine_country') {
        //     idValue = getIdFromName(apiOptions.country, value);
        // } else if (fieldKey === 'wine_region') {
        //     idValue = getIdFromName(apiOptions.region, value);
        // } else if (fieldKey === 'wine_sub_region') {
        //     idValue = getIdFromName(apiOptions.subRegion, value);
        // } else if (fieldKey === 'wine_appellation') {
        //     idValue = getIdFromName(apiOptions.Appellation, value);
        // } else if (fieldKey === 'wine_sub_appellation') {
        //     idValue = getIdFromName(apiOptions.subAppellation, value);
        // }

        // // Update the current field
        // handleFieldChange(fieldKey, 'custom', idValue);

        // Handle waterfall dependencies
        if (fieldKey === 'wine_country') {
            setMergeData(prev => {
                const newData = { ...prev };
                delete newData['wine_region'];
                delete newData['wine_sub_region'];
                delete newData['wine_appellation'];
                delete newData['wine_sub_appellation'];
                return newData;
            });
            await fetchRegionsByCountry(idValue);
        } else if (fieldKey === 'wine_region') {
            setMergeData(prev => {
                const newData = { ...prev };
                delete newData['wine_sub_region'];
                delete newData['wine_appellation'];
                delete newData['wine_sub_appellation'];
                return newData;
            });
            await fetchSubRegionsByRegion(idValue);
        } else if (fieldKey === 'wine_sub_region') {
            setMergeData(prev => {
                const newData = { ...prev };
                delete newData['wine_appellation'];
                delete newData['wine_sub_appellation'];
                return newData;
            });
            await fetchAppellationsBySubRegion(idValue);
        } else if (fieldKey === 'wine_appellation') {
            setMergeData(prev => {
                const newData = { ...prev };
                delete newData['wine_sub_appellation'];
                return newData;
            });
            await fetchSubAppellationsByAppellation(idValue);
        }
    };

    // Handle cross-field dependencies when non-custom fields are selected
    const updateLocationOptionsBasedOnSelection = async () => {
        // Get currently selected values (both custom and from wines)
        const getSelectedValue = (fieldKey: string) => {
            const fieldData = mergeData[fieldKey];
            if (!fieldData) return null;
            
            if (fieldData.source === 'custom') {
                return fieldData.value;
            } else {
                // Get from selected wine
                const wine = selectedWines.find(w => w.id.toString() === fieldData.source);
                if (!wine) return null;
                
                switch (fieldKey) {
                    case 'wine_country': return wine.wine_country.toString();
                    case 'wine_region': return wine.wine_region.toString();
                    case 'wine_sub_region': return wine.wine_location?.toString() || null;
                    case 'wine_appellation': return wine.wine_locale?.toString() || null;
                    default: return null;
                }
            }
        };

        // Use state IDs if available, otherwise fallback to selected value
        const selectedCountry = countryIdByName || getSelectedValue('wine_country');
        const selectedRegion = regionIdByName || getSelectedValue('wine_region');
        const selectedSubRegion = subRegionIdByName || getSelectedValue('wine_sub_region');
        const selectedAppellation = appellationIdByName || getSelectedValue('wine_appellation');
        const selectedSubAppellation = subAppellationIdByName || getSelectedValue('wine_sub_appellation');

        // Update regions based on selected country
        if (selectedCountry) {
            await fetchRegionsByCountry(selectedCountry);
        }

        // Update sub-regions based on selected region
        if (selectedRegion) {
            await fetchSubRegionsByRegion(selectedRegion);
        }

        // Update appellations based on selected sub-region
        if (selectedSubRegion) {
            await fetchAppellationsBySubRegion(selectedSubRegion);
        }

        // Update sub-appellations based on selected appellation
        if (selectedAppellation) {
            await fetchSubAppellationsByAppellation(selectedAppellation);
        }
    };

    // Debounced search function
    const debouncedProducerSearch = useMemo(
        () => debounce((searchTerm: string) => {
            setProducerPage(1);
            fetchProducers(1, searchTerm, true);
        }, 300),
        []
    );

    // Handle producer input change
    const handleProducerInputChange = (inputValue: string) => {
        setProducerSearchTerm(inputValue);
        if (inputValue.length >= 2) {
            debouncedProducerSearch(inputValue);
        } else if (inputValue.length === 0) {
            setProducers([]);
            setHasMoreProducers(false);
        }
    };

    // Handle loading more producers on scroll
    const handleProducerMenuScrollToBottom = () => {
        if (!isLoadingProducers && hasMoreProducers && producerSearchTerm.length >= 2) {
            fetchProducers(producerPage + 1, producerSearchTerm, false);
        }
    };

    const handleRegCodeSelect = async (regCode: string, fieldKey: string) => {
        try {
            setIsLoadingLiteral(true);
            const response = await directInputApi.getLiteral({ reg_code: regCode });
            const fetchedLiteral = response?.data?.data || '';
            setMergeData(prev => ({
                ...prev,
                [fieldKey]: {
                    ...prev[fieldKey],
                    value: JSON.stringify({
                        ...safeParse(prev[fieldKey]?.value || '{}'),
                        regCode,
                        literal: fetchedLiteral,
                        producerId: '', // clear producer
                    }),
                    source: 'custom'
                },
                // This ensures the literal field is updated
                literal: {
                    source: 'custom',
                    value: JSON.stringify({
                        ...safeParse(prev.literal?.value || '{}'),
                        literal: fetchedLiteral
                    })
                }
            }));
            setIsProducerDisabled(true);
            setIsRegCodeDisabled(false);
        } catch (error) {
            console.error('Error fetching literal by reg code:', error);
        } finally {
            setIsLoadingLiteral(false);
        }
    };

    const handleProducerSelect = async (producerOption: any, fieldKey: string) => {
        try {
            setIsLoadingLiteral(true);
            const response = await directInputApi.getLiteral({ producer_literal: producerOption.literal });
            const fetchedLiteral = response?.data?.data || '';
            setMergeData(prev => ({
                ...prev,
                [fieldKey]: {
                    ...prev[fieldKey],
                    value: JSON.stringify({
                        producerId: producerOption.value,
                        producerName: producerOption.name_show,
                        regCode: '',
                        nameShow: producerOption.name_show, // populate nameShow
                        literal: fetchedLiteral
                    }),
                    source: 'custom'
                },
                // This ensures the literal field is updated
                literal: {
                    source: 'custom',
                    value: JSON.stringify({
                        ...safeParse(prev.literal?.value || '{}'),
                        literal: fetchedLiteral
                    })
                }
            }));
            setIsProducerDisabled(false);
            setIsRegCodeDisabled(true);
        } catch (error) {
            console.error('Error fetching literal by producer:', error);
        } finally {
            setIsLoadingLiteral(false);
        }
    };

    // API Functions
    const fetchDropdownOptions = async (): Promise<void> => {
        setIsLoading(true);
        try {
            // First fetch the basic wine properties
            const [
                colorResponse,
                sweetnessResponse,
                varietyResponse,
                typeResponse,
            ] = await Promise.all([
                directInputApi.getColorList(),
                directInputApi.getSweetnessList(),
                directInputApi.getVarietyList({per_page: 999999}),
                directInputApi.getTypeList(),
            ]).catch(error => {
                console.error('Error fetching basic wine properties:', error);
                return [null, null, null, null];
            });

            // Update the options with basic wine properties
            setApiOptions(prev => ({
                ...prev,
                color: Array.isArray(colorResponse?.data?.data) ? colorResponse?.data?.data?.map((c:string) => ({ 
                    label: c, 
                    value: c 
                })) : [],
                sweetness: Array.isArray(sweetnessResponse?.data?.data) ? sweetnessResponse?.data?.data?.map((s:string) => ({ 
                    label: s, 
                    value: s 
                })) : [],
                type: Array.isArray(typeResponse?.data?.data) ? typeResponse?.data?.data?.map((t:string) => ({ 
                    label: t, 
                    value: t 
                })) : [],
                variety: varietyResponse?.data?.data?.result?.map((v:any) => ({
                    value: v.id.toString(),
                    label: v.name
                })) || [],
            }));

            // Then fetch the location-related data (only countries initially)
            const countryResponse = await countryApi.getCountryList({per_page:999999}).catch(() => ({ data: { data: { result: [] } } }));

            // Transform location-related data
            const countryArr = countryResponse?.data?.data?.result || [];
            
            // Safely access and transform the data
            setApiOptions(prev => ({
                ...prev,
                country: countryArr.map((country: any) => ({
                    value: country.id.toString(),
                    label: country.name
                })),
                // Initialize empty arrays for dependent location fields
                region: [],
                subRegion: [],
                Appellation: [],
                subAppellation: [],
            }));

            console.log('Country Options:', countryArr);
            // Update entity maps
            setCountryMap(countryArr.reduce((acc: EntityMap, c: any) => ({ ...acc, [c.id]: c.name }), {}));
            
        } catch (error) {
            console.error('Error fetching dropdown options:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Effects
    useEffect(() => {
        if (isOpen) {
            // Fetch all location data on mount
            fetchDropdownOptions();
            (async () => {
                try {
                    const [countryRes, regionRes, subRegionRes, appellationRes, subAppellationRes] = await Promise.all([
                        countryApi.getCountryList({ per_page: 999999 }),
                        regionApi.getRegionList({ per_page: 999999 }),
                        subRegionApi.getSubRegionList({ per_page: 999999 }),
                        AppellationApi.getAppellationList({ per_page: 999999 }),
                        subAppellationApi.getSubAppellationList({ per_page: 999999 }),
                    ]);
                    setApiOptions(prev => ({
                        ...prev,
                        country: (countryRes?.data?.data?.result || []).map((c: any) => ({
                            value: c.id.toString(),
                            label: c.name
                        })),
                        region: (regionRes?.data?.data?.result || []).map((r: any) => ({
                            value: r.id.toString(),
                            label: r.name,
                            region_code: r.region_code
                        })),
                        subRegion: (subRegionRes?.data?.data?.result || []).map((s: any) => ({
                            value: s.id.toString(),
                            label: s.name
                        })),
                        Appellation: (appellationRes?.data?.data?.result || []).map((a: any) => ({
                            value: a.id.toString(),
                            label: a.name
                        })),
                        subAppellation: (subAppellationRes?.data?.data?.result || []).map((sa: any) => ({
                            value: sa.id.toString(),
                            label: sa.name
                        })),
                    }));
                    setCountryMap((countryRes?.data?.data?.result || []).reduce((acc: EntityMap, c: any) => ({ ...acc, [c.id]: c.name }), {}));
                    setRegionMap((regionRes?.data?.data?.result || []).reduce((acc: EntityMap, r: any) => ({ ...acc, [r.id]: r.name }), {}));
                    setSubRegionMap((subRegionRes?.data?.data?.result || []).reduce((acc: EntityMap, s: any) => ({ ...acc, [s.id]: s.name }), {}));
                    setAppellationMap((appellationRes?.data?.data?.result || []).reduce((acc: EntityMap, a: any) => ({ ...acc, [a.id]: a.name }), {}));
                    setSubAppellationMap((subAppellationRes?.data?.data?.result || []).reduce((acc: EntityMap, sa: any) => ({ ...acc, [sa.id]: sa.name }), {}));
                    await fetchRegionRegCodes();
                } catch (error) {
                    console.error('Error fetching all location data:', error);
                }
            })();
        }
    }, [isOpen]);

     // Cleanup debounced function
    useEffect(() => {
        return () => {
            debouncedProducerSearch.cancel();
        };
    }, [debouncedProducerSearch]);

    // Effect to control body scroll
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Event Handlers
    const handleFieldChange = (
        fieldKey: string,
        source: string,
        value: string
    ): void => {
        // Only update the custom column, do not affect wine columns
        setMergeData(prevData => {
            const newData = { ...prevData };
            newData[fieldKey] = { source, value };
            return newData;
        });
    };

    // Fixed: Handle column selection/deselection properly
    const handleColumnSelect = (source: string): void => {
        const isAllSelected = FORM_FIELDS.every(field =>
            mergeData[field.key]?.source === source
        );

        if (isAllSelected) {
            // Deselect all fields for this source
            setMergeData(prevData => {
                const newData = { ...prevData };
                FORM_FIELDS.forEach(field => {
                    if (newData[field.key]?.source === source) {
                        delete newData[field.key];
                    }
                });
                return newData;
            });
        } else {
            // Select all fields for this source
            const newMergeData: Record<string, MergeDataItem> = { ...mergeData };

            FORM_FIELDS.forEach((field) => {
                if (source === "custom") {
                    newMergeData[field.key] = { source: "custom", value: "" };
                } else {
                    const selectedWine = selectedWines.find(wine =>
                        wine.id.toString() === source
                    );

                    if (selectedWine) {
                        let value = '';
                        switch (field.key) {
                            case 'wine_country': 
                                {
                                    const idValue = selectedWine.wine_country === 0 ? '' : selectedWine.wine_country?.toString();
                                    const isValidId = idValue && countryMap[Number(idValue)];
                                    value = isValidId ? idValue : '';
                                }
                                break;
                            case 'wine_region': 
                                {
                                    const idValue = selectedWine.wine_region === 0 ? '' : selectedWine.wine_region?.toString();
                                    const isValidId = idValue && regionMap[Number(idValue)];
                                    value = isValidId ? idValue : '';
                                }
                                break;
                            case 'wine_sub_region': 
                                {
                                    const idValue = (selectedWine.wine_location ?? 0) === 0 ? '' : (selectedWine.wine_location ?? '').toString();
                                    const isValidId = idValue && subRegionMap[Number(idValue)];
                                    value = isValidId ? idValue : '';
                                }
                                break;
                            case 'wine_appellation': 
                                {
                                    const idValue = (selectedWine.wine_locale ?? 0) === 0 ? '' : (selectedWine.wine_locale ?? '').toString();
                                    const isValidId = idValue && appellationMap[Number(idValue)];
                                    value = isValidId ? idValue : '';
                                }
                                break;
                            case 'wine_sub_appellation': 
                                {
                                    const idValue = (selectedWine.wine_site ?? 0) === 0 ? '' : (selectedWine.wine_site ?? '').toString();
                                    const isValidId = idValue && subAppellationMap[Number(idValue)];
                                    value = isValidId ? idValue : '';
                                }
                                break;
                            default: 
                                value = getFieldValue(selectedWine, field.key);
                        }
                        newMergeData[field.key] = {
                            source,
                            value
                        };
                    }
                }
            });

            // Special logic: If custom is selected and producer is being set to custom, also set literal to custom
            if (source === "custom" && newMergeData["producer"]) {
                newMergeData["literal"] = { source: "custom", value: "" };
            }

            setMergeData(newMergeData);
        }
    };

    // Fixed: Handle individual field selection/deselection
    const handleIndividualFieldSelect = async (fieldKey: string, source: string): Promise<void> => {
        const isCurrentlySelected = mergeData[fieldKey]?.source === source;

        if (isCurrentlySelected) {
            setMergeData(prevData => {
                const newData = { ...prevData };
                delete newData[fieldKey];
                return newData;
            });
        } else {
            if (source === "custom") {
                handleFieldChange(fieldKey, 'custom', '');
                if (fieldKey === "producer") {
                    setTimeout(() => {
                        handleFieldChange('literal', 'custom', '');
                    }, 0);
                }
            } else {
                const selectedWine = selectedWines.find(wine =>
                    wine.id.toString() === source
                );

                if (selectedWine) {
                    // Validate location hierarchy and get fields to clear
                    const fieldsToUpdate = await validateLocationHierarchy(fieldKey, source, selectedWine);
                    // Special case: if literal is custom and we're selecting producer from wine column
                    if (fieldKey === "producer" && mergeData['literal']?.source === 'custom') {
                        try {
                            setIsLoadingLiteral(true);
                            const response = await directInputApi.getLiteral({ 
                                producer_literal: selectedWine.producer?.literal || ''
                            });
                            const fetchedLiteral = response?.data?.data || '';
                            
                            // Update literal field with fetched value
                            setMergeData(prev => ({
                                ...prev,
                                literal: {
                                    source: 'custom',
                                    value: JSON.stringify({
                                        ...safeParse(prev.literal?.value || '{}'),
                                        literal: fetchedLiteral
                                    })
                                }
                            }));
                        } catch (error) {
                            console.error('Error fetching literal by producer literal:', error);
                        } finally {
                            setIsLoadingLiteral(false);
                        }
                    }

                    // Location fields special handling
                    if (
                        ["wine_country", "wine_region", "wine_sub_region", "wine_appellation", "wine_sub_appellation"].includes(fieldKey)
                    ) {
                        // Get the actual ID value from the wine object
                        let idValue = '';
                        switch (fieldKey) {
                            case 'wine_country': 
                                idValue = selectedWine.wine_country === 0 ? '' : selectedWine.wine_country?.toString(); 
                                break;
                            case 'wine_region': 
                                idValue = selectedWine.wine_region === 0 ? '' : selectedWine.wine_region?.toString(); 
                                break;
                            case 'wine_sub_region': 
                                idValue = (selectedWine.wine_location ?? 0) === 0 ? '' : (selectedWine.wine_location ?? '').toString(); 
                                break;
                            case 'wine_appellation': 
                                idValue = (selectedWine.wine_locale ?? 0) === 0 ? '' : (selectedWine.wine_locale ?? '').toString(); 
                                break;
                            case 'wine_sub_appellation': 
                                idValue = (selectedWine.wine_site ?? 0) === 0 ? '' : (selectedWine.wine_site ?? '').toString(); 
                                break;
                        }
                        
                        // Validate that the ID exists in the corresponding entity map
                        let isValidId = false;
                        if (idValue) {
                            const numericId = Number(idValue);
                            switch (fieldKey) {
                                case 'wine_country': 
                                    isValidId = !!countryMap[numericId];
                                    break;
                                case 'wine_region': 
                                    isValidId = !!regionMap[numericId];
                                    break;
                                case 'wine_sub_region': 
                                    isValidId = !!subRegionMap[numericId];
                                    break;
                                case 'wine_appellation': 
                                    isValidId = !!appellationMap[numericId];
                                    break;
                                case 'wine_sub_appellation': 
                                    isValidId = !!subAppellationMap[numericId];
                                    break;
                            }
                        }
                        
                        // Only use the ID if it's valid, otherwise use empty string
                        const finalValue = isValidId ? idValue : '';
                        handleFieldChange(fieldKey, source, finalValue);
                    } else {
                        handleFieldChange(
                            fieldKey,
                            source,
                            getFieldValue(selectedWine, fieldKey)
                        );
                    }
                    
                    // Apply hierarchy validation - clear incompatible parent fields
                    setMergeData(prevData => {
                        const newData = { ...prevData };
                        Object.keys(fieldsToUpdate).forEach(fieldToUpdate => {
                            if (fieldsToUpdate[fieldToUpdate].source === '') {
                                // Clear the incompatible field
                                delete newData[fieldToUpdate];
                            }
                        });
                        return newData;
                    });
                }
            }
            setTimeout(() => {
                updateLocationOptionsBasedOnSelection();
            }, 0);
        }
    };

    const handleSubmit = async (): Promise<void> => {
            // Collect previous wine literals
            const previous_wine_db_literals = selectedWines.map(w => w.literal);

            // Helper to map name to ID for location fields
            const mapNameToId = (options: SelectOption[], value: any) => {
                if (typeof value === 'string' && isNaN(Number(value))) {
                    const found = options.find(opt => opt.label === value);
                    return found ? found.value : value;
                }
                return value;
            };

            // Helper to get value from mergeData
            const getCustomField = (key: string) => {
                if (mergeData[key]?.source === 'custom') {
                    if (key === 'producer') {
                        return safeParse(mergeData[key]?.value || '{}');
                    }
                    if (key === 'literal') {
                        return safeParse(mergeData[key]?.value || '{}').literal || '';
                    }
                    // For location fields, map name to ID if needed
                    if (["wine_country", "wine_region", "wine_sub_region", "wine_appellation", "wine_sub_appellation", "variety"].includes(key)) {
                        let options: SelectOption[] = [];
                        switch (key) {
                            case "wine_country": options = apiOptions.country; break;
                            case "wine_region": options = apiOptions.region; break;
                            case "wine_sub_region": options = apiOptions.subRegion; break;
                            case "wine_appellation": options = apiOptions.Appellation; break;
                            case "wine_sub_appellation": options = apiOptions.subAppellation; break;
                            case "variety": options = apiOptions.variety; break;
                            default: break;
                        }
                        return mapNameToId(options, mergeData[key]?.value || '');
                    }
                    return mergeData[key]?.value || '';
                }
                // If not custom, get from selected wine and return the appropriate value
                const srcId = mergeData[key]?.source;
                const wine = selectedWines.find(w => w.id.toString() === srcId);
                if (!wine) return '';
                // For location fields, map name to ID if needed
                if (["wine_country", "wine_region", "wine_sub_region", "wine_appellation", "wine_sub_appellation", "variety"].includes(key)) {
                    let value: any = '';
                    switch (key) {
                        case 'wine_country': 
                            {
                                const idValue = wine.wine_country === 0 ? '' : wine.wine_country;
                                value = idValue && countryMap[Number(idValue)] ? idValue : '';
                            }
                            break;
                        case 'wine_region': 
                            {
                                const idValue = wine.wine_region === 0 ? '' : wine.wine_region;
                                value = idValue && regionMap[Number(idValue)] ? idValue : '';
                            }
                            break;
                        case 'wine_sub_region': 
                            {
                                const idValue = (wine.wine_location ?? 0) === 0 ? '' : wine.wine_location;
                                value = idValue && subRegionMap[Number(idValue)] ? idValue : '';
                            }
                            break;
                        case 'wine_appellation': 
                            {
                                const idValue = (wine.wine_locale ?? 0) === 0 ? '' : wine.wine_locale;
                                value = idValue && appellationMap[Number(idValue)] ? idValue : '';
                            }
                            break;
                        case 'wine_sub_appellation': 
                            {
                                const idValue = (wine.wine_site ?? 0) === 0 ? '' : wine.wine_site;
                                value = idValue && subAppellationMap[Number(idValue)] ? idValue : '';
                            }
                            break;
                        case 'variety': 
                            {
                                const idValue = wine.variety_id === 0 ? '' : wine.variety_id;
                                const found = apiOptions.variety.find((v: any) => String(v.value) === String(idValue));
                                value = found ? idValue : '';
                            }
                            break;
                        default: value = '';
                    }
                    let options: SelectOption[] = [];
                    switch (key) {
                        case "wine_country": options = apiOptions.country; break;
                        case "wine_region": options = apiOptions.region; break;
                        case "wine_sub_region": options = apiOptions.subRegion; break;
                        case "wine_appellation": options = apiOptions.Appellation; break;
                        case "wine_sub_appellation": options = apiOptions.subAppellation; break;
                        case "variety": options = apiOptions.variety; break;
                        default: break;
                    }
                    return mapNameToId(options, value);
                }
                switch (key) {
                    case 'producer': return { producerId: wine.producer_id === 0 ? '' : wine.producer_id, nameShow: wine.producer?.name_show || '' };
                    case 'literal': return wine.literal || '';
                    case 'color': return wine.color || '';
                    case 'sweetness': return wine.sweetness || '';
                    case 'type': return wine.type || '';
                    case 'name': return wine.name || '';
                    default: return getFieldValue(wine, key);
                }
            };

            // Build new_wine_db - only include fields with non-zero values
            let new_wine_db: any = {};

            // Add string fields if they have values
            const literal = getCustomField('literal');
            if (literal) {
                new_wine_db.literal = literal;
            }

            const labelName = getCustomField('name');
            if (labelName) {
                new_wine_db.label_name = labelName;
            }

            const color = getCustomField('color');
            if (color) {
                new_wine_db.color = color;
            }

            const sweetness = getCustomField('sweetness');
            if (sweetness) {
                new_wine_db.sweetness = sweetness;
            }

            const type = getCustomField('type');
            if (type) {
                new_wine_db.type = type;
            }

            // Fill numeric fields only if they are non-zero
            const numericFieldMap = {
                'variety': 'variety',
                'wine_country': 'country',
                'wine_region': 'region',
                'wine_sub_region': 'sub_region',
                'wine_appellation': 'appellation',
                'wine_sub_appellation': 'sub_appellation'
            };

            Object.entries(numericFieldMap).forEach(([sourceKey, targetKey]) => {
                let val = getCustomField(sourceKey);
                if (typeof val === 'object' && val !== null && 'value' in val) val = val.value;
                const numericVal = Number(val);
                // Only add to payload if the value is not 0
                if (numericVal && numericVal !== 0) {
                    new_wine_db[targetKey] = numericVal;
                }
            });

            // Producer logic - only add producer_id if it's not 0
            let producerInfo = getCustomField('producer');
            let isCustomLiteral = mergeData['literal']?.source === 'custom';
            let isCustomProducer = mergeData['producer']?.source === 'custom';
            let isRegCode = producerInfo && producerInfo.regCode && !producerInfo.producerId;
            
            if (isCustomLiteral && isCustomProducer && isRegCode) {
                // Call producer create API
                try {
                    const payload = {
                        literal: (new_wine_db.literal || '').substring(0, 6),
                        producer_show: producerInfo.nameShow || ''
                    };
                    const resp = await directInputApi.createProducer(payload);
                    const producer_id = resp?.data?.data?.id || 0;
                    // Only add if producer_id is not 0
                    if (producer_id && producer_id !== 0) {
                        new_wine_db.producer_id = producer_id;
                    }
                } catch (err) {
                    console.error('Error creating producer:', err);
                    return;
                }
            } else if (!isCustomLiteral && !isCustomProducer) {
                // Use chosen wine's producer_id
                const srcId = mergeData['producer']?.source;
                const wine = selectedWines.find(w => w.id.toString() === srcId);
                const producerId = wine ? wine.producer?.id : 0;
                // Only add if producer_id is not 0
                if (producerId && producerId !== 0) {
                    new_wine_db.producer_id = producerId;
                }
            } else if (isCustomLiteral && !isCustomProducer) {
                // Special case: literal is custom but producer is from wine column
                // This happens when we populate literal by selecting producer from wine column
                const producerSrcId = mergeData['producer']?.source;
                const wine = selectedWines.find(w => w.id.toString() === producerSrcId);
                const producerId = wine ? wine.producer?.id : 0;
                // Only add if producer_id is not 0
                if (producerId && producerId !== 0) {
                    new_wine_db.producer_id = producerId;
                }
            } else {
                // Use selected producer_id from custom
                const producerId = Number(producerInfo.producerId || producerInfo.value || 0);
                // Only add if producer_id is not 0
                if (producerId && producerId !== 0) {
                    new_wine_db.producer_id = producerId;
                }
            }

            // Submit merge payload
            const payload = {
                previous_wine_db_literals,
                new_wine_db
            };
            try {
                const response = await directInputApi.mergeWines(payload);
                
                // Show success toast with response message
                toast.success(response?.data?.message || 'Wines merged successfully');
                
                // Call the success callback if provided
                if (onMergeSuccess) {
                    onMergeSuccess();
                } else {
                    onClose();
                }
            } catch (err: any) {
                console.error('Error merging wines:', err);
                // Show error toast with error message
                toast.error(err?.response?.data?.message || err?.message || 'Failed to merge wines');
            }
            console.log('Merge payload for testing:', payload);
    };

    // getFieldValue function
    const getFieldValue = (wine: Wine, fieldKey: string): string => {
        // Always use the wine's original value and entity maps for display, never custom field value
        if (fieldKey === "wine_country") {
            const val = wine.wine_country;
            // Don't show 0 values or values that don't exist in countryMap
            if (val === 0 || !countryMap[val]) return '';
            return countryMap[val];
        }
        if (fieldKey === "wine_region") {
            const val = wine.wine_region;
            // Don't show 0 values or values that don't exist in regionMap
            if (val === 0 || !regionMap[val]) return '';
            return regionMap[val];
        }
        if (fieldKey === "wine_sub_region") {
            const val = wine.wine_location ?? 0;
            // Don't show 0 values or values that don't exist in subRegionMap
            if (val === 0 || !subRegionMap[val]) return '';
            return subRegionMap[val];
        }
        if (fieldKey === "wine_appellation") {
            const val = wine.wine_locale ?? 0;
            // Don't show 0 values or values that don't exist in appellationMap
            if (val === 0 || !appellationMap[val]) return '';
            return appellationMap[val];
        }
        if (fieldKey === "wine_sub_appellation") {
            const val = wine.wine_site ?? 0;
            // Don't show 0 values or values that don't exist in subAppellationMap
            if (val === 0 || !subAppellationMap[val]) return '';
            return subAppellationMap[val];
        }
        if (fieldKey === "variety") {
            // Don't show 0 values for variety_id
            if (wine.variety_id === 0) return '';
            const found = apiOptions.variety.find((v: any) => String(v.value) === String(wine.variety_id));
            return found ? found.label : '';
        }
        if (fieldKey === "producer") return wine.producer?.name_show || '';
        if (fieldKey === "literal") return wine.literal || '';
        if (fieldKey === "name") return wine.name || '';
        if (fieldKey === "color") return wine.color || '';
        if (fieldKey === "type") return wine.type || '';
        if (fieldKey === "sweetness") return wine.sweetness || '';
        return "";
    };

    // Helper Functions for Rendering
    const getFieldOptions = (fieldKey: string): SelectOption[] => {
        if (["color", "sweetness", "type"].includes(fieldKey)) {
            const apiField = fieldKey as keyof Pick<ApiOptions, "color" | "sweetness" | "type">;
            return apiOptions[apiField];
        }
        if (fieldKey === "variety") {
            return apiOptions.variety;
        }
        if (fieldKey === "wine_country") {
            return apiOptions.country;
        }
        if (fieldKey === "wine_region") {
            return apiOptions.region;
        }
        if (fieldKey === "wine_sub_region") {
            return apiOptions.subRegion;
        }
        if (fieldKey === "wine_appellation") {
            return apiOptions.Appellation;
        }
        if (fieldKey === "wine_sub_appellation") {
            return apiOptions.subAppellation;
        }
        return [];
    };

    const safeParse = (value: string) => {
        try {
            return JSON.parse(value);
        } catch {
            return {};
        }
    };

    // Early return if modal is not open
    if (!isOpen) return null;

    // Responsive modal width calculation based on number of wines
    const getModalStyles = () => {
        // Fixed minimum widths for columns
        const minFieldColumnWidth = 200;
        const minCustomColumnWidth = 250;
        const minWineColumnWidth = 300;

        // Calculate wine columns total width
        const totalWineWidth = selectedWines.length * minWineColumnWidth;

        // Calculate base width needed for the modal
        const baseWidth = minFieldColumnWidth + minCustomColumnWidth + totalWineWidth;

        // Add some padding between columns
        const padding = (selectedWines.length + 2) * 20; // 20px padding between columns

        // Calculate final width with padding
        const calculatedWidth = baseWidth + padding;

        // Ensure the width doesn't exceed viewport
        const maxWidth = window.innerWidth * 0.98;
        const finalWidth = Math.min(calculatedWidth, maxWidth);

        const styles = {
            width: `${finalWidth}px`,
            maxWidth: '98vw',
            height: '98vh',
            maxHeight: '98vh'
        };

        return styles;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" id="mergeWinesModal">
            <div
                className="bg-white rounded-lg shadow-xl flex flex-col"
                style={getModalStyles()}
            >
                {/* Modal Header */}
                <div className="relative flex items-center justify-center border-b border-gray-200 p-4 flex-shrink-0">
                    <h2 className="text-lg font-bold text-gray-800">Merge Wines</h2>
                    <button
                        onClick={onClose}
                        className="absolute right-4 p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <CloseIcon className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Modal Content - Made to fill available space */}
                <div className="flex-1 overflow-hidden flex flex-col min-h-0 relative">
                    {isLoading && (
                        <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-10">
                            <LoadingSpinner />
                        </div>
                    )}

                    {/* Table Container - Fills remaining space */}
                    <div className={`flex-1 flex flex-col min-h-0 px-4 ${isLoading ? "pointer-events-none opacity-50" : ""}`}>
                        <div className="flex-1 overflow-auto min-h-0">
                            <table className="w-full divide-y divide-gray-200 table-fixed h-full">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[200px] sticky left-0 bg-gray-50 z-20">
                                            Field
                                        </th>
                                        {selectedWines.map((wine, index) => (
                                            <th key={wine.id} className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[300px]">
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={FORM_FIELDS.every(field => mergeData[field.key]?.source === wine.id.toString())}
                                                        onChange={() => handleColumnSelect(wine.id.toString())}
                                                        className="h-3 w-3 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                                                    />
                                                    <span className="truncate">Wine {index + 1}</span>
                                                </div>
                                            </th>
                                        ))}
                                        <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[300px] sticky right-0 bg-gray-50 z-20">
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    checked={FORM_FIELDS.every(field => mergeData[field.key]?.source === 'custom')}
                                                    onChange={() => handleColumnSelect('custom')}
                                                    className="h-3 w-3 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                                                />
                                                <span>Custom Input</span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {FORM_FIELDS.map((field, fieldIndex) => (
                                       <tr key={field.key} className="hover:bg-gray-50">
                                            <td className="px-1 py-1 text-xs font-medium text-gray-900 sticky left-0 bg-transparent z-10">
                                                <div className="truncate" title={field.label}>
                                                    {field.label}
                                                </div>
                                            </td>
                                            {selectedWines.map((wine) => (
                                                <td key={wine.id} className="px-1 py-1 bg-transparent">
                                                    <div
                                                        className="flex items-center space-x-2 h-full cursor-pointer"
                                                        onClick={() => handleIndividualFieldSelect(field.key, wine.id.toString())}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyPress={e => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                handleIndividualFieldSelect(field.key, wine.id.toString());
                                                            }
                                                        }}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={field.key}
                                                            checked={mergeData[field.key]?.source === wine.id.toString()}
                                                            readOnly
                                                            className="h-3 w-3 text-blue-600 border-gray-300 focus:ring-blue-500 flex-shrink-0 cursor-pointer"
                                                            tabIndex={-1}
                                                        />
                                                        <span className="text-xs text-gray-600 break-words flex-1 min-w-0" title={getFieldValue(wine, field.key)}>
                                                            {getFieldValue(wine, field.key)}
                                                        </span>
                                                    </div>
                                                </td>
                                            ))}
                                            <td className="px-1 py-1 sticky right-0 bg-transparent z-10">
                                                <div className="flex items-center space-x-2 h-full">
                                                    {/* Only radio and label are clickable for selection */}
                                                    <div
                                                        className="flex items-center cursor-pointer"
                                                        onClick={() => handleIndividualFieldSelect(field.key, 'custom')}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyPress={e => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                handleIndividualFieldSelect(field.key, 'custom');
                                                            }
                                                        }}
                                                        style={{ userSelect: 'none' }}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={field.key}
                                                            checked={mergeData[field.key]?.source === 'custom'}
                                                            readOnly
                                                            className="h-3 w-3 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                                                            tabIndex={-1}
                                                        />
                                                        <span className="text-xs text-gray-600">{/* Optionally add label here */}</span>
                                                    </div>
                                                    {/* Input/select fields are NOT clickable for radio selection */}
                                                    <div className="flex-1 min-w-0">
                                                        {field.key === 'literal' ? (
                                                            <input
                                                                id="inputTextField"
                                                                type="text"
                                                                disabled={true}
                                                                placeholder="ID"
                                                                value={mergeData[field.key]?.source === 'custom'
                                                                    ? safeParse(mergeData[field.key]?.value || '{}').literal || ''
                                                                    : ''
                                                                }
                                                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                                                            />
                                                        ) : field.type === 'producer' ? (
                                                            <div className="flex flex-col gap-1">
                                                                <Select
                                                                    isDisabled={mergeData[field.key]?.source !== 'custom' || isProducerDisabled}
                                                                    isClearable
                                                                    value={mergeData[field.key]?.source === 'custom'
                                                                        ? producers.find(p => p.value === safeParse(mergeData[field.key]?.value || '{}').producerId)
                                                                        : null
                                                                    }
                                                                    onChange={(option) => {
                                                                        if (option) handleProducerSelect(option, field.key);
                                                                        else {handleProducerSelect(null, field.key);
                                                                        setMergeData(prev => ({
                                                                            ...prev,
                                                                            [field.key]: {
                                                                                source: 'custom',
                                                                                value: JSON.stringify({
                                                                                    producerId: '',
                                                                                    producerName: '',
                                                                                    regCode: '',
                                                                                    nameShow: '',
                                                                                    literal: ''
                                                                                })
                                                                            },
                                                                            literal: {
                                                                                source: 'custom',
                                                                                value: JSON.stringify({ literal: '' })
                                                                            }
                                                                        }));
                                                                        setIsRegCodeDisabled(false);
                                                                        setIsProducerDisabled(false);
                                                                    }}}
                                                                    options={producers}
                                                                    isSearchable
                                                                    placeholder="Type to search producer..."
                                                                    onInputChange={handleProducerInputChange}
                                                                    onMenuScrollToBottom={handleProducerMenuScrollToBottom}
                                                                    isLoading={isLoadingProducers}
                                                                    loadingMessage={() => "Searching producers..."}
                                                                    noOptionsMessage={({ inputValue }) => 
                                                                        inputValue.length < 2 
                                                                            ? "Type at least 2 characters to search" 
                                                                            : isLoadingProducers 
                                                                                ? "Searching..." 
                                                                                : "No producers found"
                                                                    }
                                                                    className="text-xs"
                                                                    classNamePrefix="select-small"
                                                                    menuPortalTarget={document.body}
                                                                    styles={{
                                                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                                        control: base => ({
                                                                            ...base,
                                                                            minHeight: '28px',
                                                                            height: '28px'
                                                                        })
                                                                    }}
                                                                />
                                                                <Select
                                                                    isDisabled={mergeData[field.key]?.source !== 'custom' || isRegCodeDisabled || !!safeParse(mergeData[field.key]?.value || '{}').producerId}
                                                                    isClearable
                                                                    value={
                                                                        mergeData[field.key]?.source === 'custom'
                                                                            ? safeParse(mergeData[field.key]?.value || '{}').regCode
                                                                                ? { value: safeParse(mergeData[field.key]?.value || '{}').regCode, label: safeParse(mergeData[field.key]?.value || '{}').regCode }
                                                                                : (
                                                                                    (mergeData[field.key]?.source !== 'custom' || isRegCodeDisabled || !!safeParse(mergeData[field.key]?.value || '{}').producerId)
                                                                                        ? { value: '', label: 'Select reg code...' }
                                                                                        : null
                                                                                )
                                                                            : null
                                                                    }
                                                                    onChange={(option) => {
                                                                        if (option) handleRegCodeSelect(option.value, field.key);
                                                                        else {
                                                                            setMergeData(prev => ({
                                                                                ...prev,
                                                                                [field.key]: {
                                                                                    source: 'custom',
                                                                                    value: JSON.stringify({
                                                                                        producerId: '',
                                                                                        producerName: '',
                                                                                        regCode: '',
                                                                                        nameShow: '',
                                                                                        literal: ''
                                                                                    })
                                                                                },
                                                                                literal: {
                                                                                    source: 'custom',
                                                                                    value: JSON.stringify({ literal: '' })
                                                                                }
                                                                            }));
                                                                            setIsProducerDisabled(false);
                                                                            setIsRegCodeDisabled(false)
                                                                        }
                                                                    }}
                                                                    options={regCodeOptions}
                                                                    placeholder="Select reg code..."
                                                                    className="text-xs"
                                                                    classNamePrefix="select-small"
                                                                    menuPortalTarget={document.body}
                                                                    styles={{
                                                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                                        control: base => ({
                                                                            ...base,
                                                                            minHeight: '28px',
                                                                            height: '28px'
                                                                        })
                                                                    }}
                                                                />
                                                                <input
                                                                    id="inputTextField"
                                                                    type="text"
                                                                    disabled={
                                                                        mergeData[field.key]?.source !== 'custom' ||
                                                                        !!safeParse(mergeData[field.key]?.value || '{}').producerId // disable if producer selected
                                                                    }
                                                                    placeholder="Name Show"
                                                                    value={mergeData[field.key]?.source === 'custom'
                                                                        ? safeParse(mergeData[field.key]?.value || '{}').nameShow || ''
                                                                        : ''
                                                                    }
                                                                    onChange={(e) => handleFieldChange(
                                                                        field.key,
                                                                        'custom',
                                                                        JSON.stringify({
                                                                            ...safeParse(mergeData[field.key]?.value || '{}'),
                                                                            nameShow: e.target.value
                                                                        })
                                                                    )}
                                                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                                    readOnly={!!safeParse(mergeData[field.key]?.value || '{}').producerId}
                                                                />
                                                            </div>
                                                        ) : field.type === 'location-select' ? (
                                                            <Select
                                                                isDisabled={mergeData[field.key]?.source !== 'custom'}
                                                                isClearable
                                                                value={mergeData[field.key]?.source === 'custom'
                                                                    ? getFieldOptions(field.key).find(
                                                                        opt => opt.value === mergeData[field.key]?.value
                                                                    )
                                                                    : null
                                                                }
                                                                onChange={(option) => {
                                                                    if (option) handleLocationFieldChange(field.key, option.value);
                                                                    else handleLocationFieldChange(field.key, '');
                                                                }}
                                                                options={getFieldOptions(field.key)}
                                                                placeholder={`Select ${field.label}...`}
                                                                isLoading={
                                                                    (field.key === 'wine_region' && isLoadingRegions) ||
                                                                    (field.key === 'wine_sub_region' && isLoadingSubRegions) ||
                                                                    (field.key === 'wine_appellation' && isLoadingAppellations) ||
                                                                    (field.key === 'wine_sub_appellation' && isLoadingSubAppellations)
                                                                }
                                                                className="text-xs"
                                                                classNamePrefix="select-small"
                                                                menuPlacement="auto"
                                                                menuPosition="fixed"
                                                                menuPortalTarget={document.body}
                                                                styles={{
                                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                                    control: base => ({
                                                                        ...base,
                                                                        minHeight: '28px',
                                                                        height: '28px'
                                                                    })
                                                                }}
                                                            />
                                                        ) : field.type === 'select' ? (
                                                            <Select
                                                                isDisabled={mergeData[field.key]?.source !== 'custom'}
                                                                isClearable
                                                                value={mergeData[field.key]?.source === 'custom'
                                                                    ? getFieldOptions(field.key).find(
                                                                        opt => opt.value === mergeData[field.key]?.value
                                                                    )
                                                                    : null
                                                                }
                                                                onChange={(option) => {
                                                                    if (option) handleFieldChange(field.key, 'custom', option.value);
                                                                    else handleFieldChange(field.key, 'custom', '');
                                                                }}
                                                                options={getFieldOptions(field.key)}
                                                                placeholder={field.label}
                                                                className="text-xs"
                                                                classNamePrefix="select-small"
                                                                menuPlacement="auto"
                                                                menuPosition="fixed"
                                                                menuPortalTarget={document.body}
                                                                styles={{
                                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                                    control: base => ({
                                                                        ...base,
                                                                        minHeight: '28px',
                                                                        height: '28px'
                                                                    })
                                                                }}
                                                            />
                                                        ) : (
                                                            <input
                                                                id="inputTextField"
                                                                type="text"
                                                                disabled={mergeData[field.key]?.source !== 'custom'}
                                                                value={mergeData[field.key]?.source === 'custom'
                                                                    ? mergeData[field.key]?.value || ''
                                                                    : ''
                                                                }
                                                                onChange={(e) => handleFieldChange(
                                                                    field.key,
                                                                    'custom',
                                                                    e.target.value
                                                                )}
                                                                placeholder={field.key === 'name' ? 'Name' : field.label}
                                                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Action Buttons - Fixed at bottom */}
                        <div className="flex justify-end pt-4 pb-4 border-t border-gray-200 bg-white flex-shrink-0">
                            <Button onClick={handleSubmit} variant="dark" size="sm">
                                Submit
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MergeWinesModal;